import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useRef, type ReactNode } from 'react';
import { useDrop } from 'react-dnd';
import { useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { styled, alpha } from '@mui/material/styles';
import cx from 'classnames';

import {
  documentCategorizationAllowedSelector,
  subtypeFilterSelector,
  setSubtypeFilterAction,
  documentsChangeTypesAction,
} from 'domain/documents';
import { useWorkspaceCategorization } from 'pages/company/WorkspaceRoute/hooks/useWorkspaceAggregations';
import { useCanReclassify } from 'pages/company/WorkspaceRoute/hooks/useCanReclassify';
import CategorizationSkeletons from 'pages/company/WorkspaceRoute/components/CategorizationSkeletons';
import { rootCategoriesListSelector } from 'domain/categories';
import { isInboxCategory, findCatById } from 'domain/categories/helpers';
import { DOCUMENT } from 'components/DnDItem';
import toast from 'components/Toast';

// Inbox-only sub-type chip row (spec da-19295 req6). Renders the present sub-type facet from the
// search_new aggregations block (backend-driven labels, incl. the All default and Unclassified).
// A chip click sets the server sub-type filter (task 3.3). A specific sub-type chip is also a
// reclassify drop target (task 4.1): dropping a document — or the current selection — assigns that
// sub-type. The All and Unclassified chips are not drop targets (base-financial goes via the tab).

const ChipRow = styled(Stack, { label: 'WorkspaceSubtypeChips' })(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 0, 1, 0),
}));

// DA-19718: paint the drag-over highlight on the chip's OWN border box, not as an outline on this
// wrapper. The old wrapper outline used a fixed 16px radius and was drawn outside the chip, so it
// never matched the small chip's 8px border radius — the mismatched, over-rounded double-ring in
// the bug. An `outline` on `.MuiChip-root` (with a negative offset so it hugs the edge, same
// technique as WorkspaceDoctypeTabs) follows the chip's exact radius and, unlike a `border`, takes
// no layout space — so it can't shift the row when hovering the borderless filled (selected) chip.
const DropSpan = styled('span', { label: 'WorkspaceSubtypeChipDrop' })(({ theme }) => ({
  display: 'inline-flex',
  '&.is-drag-over .MuiChip-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    outline: `1px dashed ${theme.palette.primary.main}`,
    outlineOffset: -1,
  },
}));

// DA-19731: same purpose as the doctype-tabs skeleton — hold the chip row's box while the block is
// still in flight, so the sticky subheader doesn't collapse and re-expand. Reuses the real ChipRow
// as the wrapper, so its padding can't drift; only the placeholder shapes are shared.
const ChipsSkeleton = () => (
  <ChipRow role="status" aria-busy="true">
    <CategorizationSkeletons count={4} width={88} height={24} radius={16} />
  </ChipRow>
);

// A specific sub-type chip is a reclassify drop target (spec 4.1): drop writes that sub-type id.
// The All chip (id:null) and the Unclassified facet are NOT targets — base-financial reclassify
// goes through the Financial tab. Always a bulk write over the current selection.
const DroppableChip = ({
  writeType,
  subtypeLabel,
  selectedIds,
  clearSelection,
  children,
}: {
  writeType: string;
  subtypeLabel: string;
  selectedIds: string[];
  clearSelection: () => void;
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLSpanElement>(null);
  const canReclassify = useCanReclassify(writeType);
  // The drag item comes from the legacy DnDItemSource (components/DnDItem); its runtime shape is
  // `{ id, ...params, itemType }` and we read `id`. `type` is not on the item — it is here only
  // because react-dnd v7's useDrop generic must extend DragObjectWithType ({ type }).
  const [{ isOver, canDrop }, drop] = useDrop<{ type: string; id: string }, unknown, { isOver: boolean; canDrop: boolean }>({
    accept: DOCUMENT,
    // DA-19719: the chip the document is already assigned to is not a drop target. Same id set as
    // the drop below, so the guard and the write can't disagree.
    canDrop: (item) => Boolean(item?.id) && canReclassify(selectedIds.length ? selectedIds : [item.id]),
    drop: (item) => {
      if (!item?.id) return;
      const documentsIds = selectedIds.length ? selectedIds : [item.id];
      new Promise((resolve, reject) => {
        dispatch({ type: documentsChangeTypesAction.type, payload: { documentsIds, type: writeType, resolve, reject } });
      })
        // DA-19760: clear the selection (and with it the bulk panel) on success — see WorkspaceDoctypeTabs.
        .then(() => {
          toast.info(
            formatMessage(
              { id: 'document.actions.subtype.success', defaultMessage: 'Document marked as {subtype}' },
              { subtype: subtypeLabel },
            ),
          );
          clearSelection();
        })
        .catch(() => null);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  drop(ref);
  return (
    <DropSpan ref={ref} className={cx({ 'is-drag-over': isOver && canDrop })}>
      {children}
    </DropSpan>
  );
};

const WorkspaceSubtypeChips = ({
  selectedIds = [],
  clearSelection,
}: {
  selectedIds?: string[];
  clearSelection: () => void;
}) => {
  const dispatch = useDispatch();
  const allowed = useSelector(documentCategorizationAllowedSelector);
  const { subtypes, isLoading } = useWorkspaceCategorization();
  const selected = useSelector(subtypeFilterSelector);
  const rootCategoriesList = useSelector(rootCategoriesListSelector);
  const { category1 } = useParams();

  const isInbox = isInboxCategory(findCatById(rootCategoriesList, category1));

  // DA-19691: no destination-lacks-sub-type reset here. The backend now pins the active sub-type
  // chip even at zero matches, so the selection stays present+highlighted across Inbox navigation
  // (parity with tile). Leaving Inbox still clears it via the category1-keyed reset (§7.3).
  if (!allowed || !isInbox) return null;
  // DA-19731: an empty list mid-fetch is not "this category has no sub-types" — see the doctype tabs.
  if (subtypes.length === 0) return isLoading ? <ChipsSkeleton /> : null;

  // Toggle: re-click the active chip clears the filter. The backend "All" chip (id:null) always
  // clears it. The saga refetches on this action.
  const onChipClick = (id: string | null) => dispatch(setSubtypeFilterAction(selected === id ? null : id));

  return (
    <ChipRow>
      {subtypes.map(({ id, label }) => {
        const chip = (
          <Chip
            label={label}
            size="small"
            variant={selected === id ? 'filled' : 'outlined'}
            color={selected === id ? 'primary' : 'default'}
            onClick={() => onChipClick(id)}
          />
        );
        // All chip (id:null) and the Unclassified facet are not drop targets (base-financial
        // reclassify goes via the Financial tab); only specific sub-types accept a drop.
        if (id === null || id === '_S_IS_UNCLASSIFIED') return <span key={id ?? 'all'}>{chip}</span>;
        return (
          <DroppableChip
            key={id}
            writeType={id}
            subtypeLabel={label}
            selectedIds={selectedIds}
            clearSelection={clearSelection}
          >
            {chip}
          </DroppableChip>
        );
      })}
    </ChipRow>
  );
};

export default WorkspaceSubtypeChips;
